import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { productAPI } from '../../api/services'; 
import SearchBar from '../search/SearchBar'; 

export default function Navbar() {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false); 
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // LIVE SUGGESTIONS STATES
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Force close menus and dropdown when route changes
  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setSearchOpen(false);
    setShowDropdown(false);
    setSearchInput('');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => { document.body.style.overflow = 'auto'; };
  }, [menuOpen]);

  // Click outside to close search predictions
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setMenuOpen(false);
    navigate('/');
  }, [logout, navigate]);

  // INPUT SEARCH DEBOUNCE DISPATCHER
  useEffect(() => {
    if (searchInput.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const { data } = await productAPI.getAll({ search: searchInput.trim(), limit: 10 });
        setSuggestions(data.products || []);
        setShowDropdown(true);
      } catch (err) {
        console.error("Instant search dropout:", err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setShowDropdown(false);
      navigate(`/products?search=${encodeURIComponent(searchInput.trim())}`);
    }
  };

  return (
    <>
      {/* STRUCTURAL WRAPPER BOX */}
      <div className="relative w-full h-[120px] lg:h-[152px] bg-[#04110c]">
        
        {/* GLOBAL HEADER WRAPPER FRAME (FIXED ON TOP) */}
        <div className="fixed top-0 left-0 right-0 z-[1000] flex flex-col shadow-xl">
          
          {/* DECK 1: TOP PREMIUM ANNOUNCEMENT TICKER STRIP */}
          <div className="w-full bg-[#f3ece4] text-[#4a3b32] py-2 text-center text-xs font-body font-medium tracking-wide border-b border-[#ebdcd0] px-4 truncate">
            Shop now Launching Offer Started🛍️
          </div>

          {/* MAIN NAVIGATION BLOCK */}
          <nav className={`w-full border-b transition-colors duration-300 relative ${
            scrolled || menuOpen
              ? 'bg-[#04110c]/98 backdrop-blur-xl border-green-800/30' 
              : 'bg-[#04110c]/95 backdrop-blur-md border-white/5'
          }`}>
            
            {/* DECK 2: MIDDLE BRANDING & FUNCTIONAL GRID */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-4 md:gap-8 h-20">
              
              {/* 📱 MOBILE VIEW LEFT NODE: HAMBURGER + SEARCH TRIGGER */}
              <div className="flex lg:hidden items-center gap-4 flex-1 justify-start">
                <button className="text-cream/70 hover:text-gold-400 transition-colors" onClick={() => setMenuOpen(!menuOpen)}>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {menuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>

                <button onClick={() => setSearchOpen(true)} className="text-cream/70 hover:text-gold-400 transition-colors" aria-label="Search">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              </div>

              {/* 💻 LOGO */}
              <div className="flex flex-shrink-0 justify-center lg:justify-start">
                <Link to="/" className="flex flex-col leading-none group text-center lg:text-left">
                  <span className="font-display text-xl sm:text-2xl text-cream tracking-wider group-hover:text-gold-400 transition-colors duration-300">Just Original</span>
                  <span className="text-gold-500 text-[8px] tracking-[0.4em] uppercase font-body mt-0.5">Luxury Perfumery</span>
                </Link>
              </div>

              {/* 💻 DESKTOP SEARCH BAR */}
              <div className="hidden lg:flex flex-1 max-w-xl relative" ref={dropdownRef}>
                <form onSubmit={handleSearchSubmit} className="w-full relative">
                  <input 
                    type="text" 
                    placeholder="Search your signature scent, notes, occasions..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onFocus={() => searchInput.trim().length >= 2 && setShowDropdown(true)}
                    className="w-full bg-white/[0.04] border border-white/10 text-cream placeholder-cream/25 text-xs px-5 py-3 rounded-full focus:outline-none focus:border-[#c4a44d] focus:bg-white/[0.08] transition-all font-body tracking-wide"
                  />
                  <button type="submit" className="absolute right-5 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold-400 transition-colors">
                    {searchLoading ? (
                      <span className="w-3.5 h-3.5 block border-2 border-gold rounded-full border-t-transparent animate-spin" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </button>
                </form>

                {/* SEARCH DROPDOWN */}
                {showDropdown && (
                  <div className="absolute top-14 left-0 right-0 w-[95vw] md:w-[75vw] lg:w-[80vw] xl:w-[70vw] bg-[#04110c] border border-gold/15 shadow-2xl p-6 rounded-2xl z-[2000] -translate-x-1/4 lg:-translate-x-1/3 max-h-[75vh] overflow-y-auto backdrop-blur-2xl">
                    <p className="text-gold text-[10px] tracking-[0.25em] uppercase font-body font-medium mb-4 border-b border-white/5 pb-2">SUGGESTED SCENTS:</p>
                    {suggestions.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {suggestions.map((product) => (
                          <a key={product._id} href={`#/products/${product.slug || product._id}`} className="flex items-center gap-3.5 p-3 rounded-xl border border-white/5 bg-white/[0.01] hover:border-gold/30 hover:bg-white/[0.04] transition-all group">
                            <div className="w-12 h-15 rounded-lg overflow-hidden bg-black/40 border border-white/5 flex-shrink-0">
                              {product.images?.[0]?.url ? (
                                <img src={product.images[0].url} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center font-display text-xs text-gold/30">JO</div>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-[9px] text-gold/40 tracking-wider uppercase font-body truncate mb-0.5 capitalize">{product.category} · {product.concentration || 'EDP'}</p>
                              <h4 className="text-cream text-xs font-display tracking-wide font-light truncate group-hover:text-gold transition-colors">{product.name}</h4>
                              <div className="flex items-baseline gap-2 mt-0.5">
                                <span className="text-gold text-xs font-body font-medium">₹{product.price?.toLocaleString()}</span>
                              </div>
                            </div>
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="text-cream/30 text-xs font-body py-4 text-center font-light tracking-wide">No matches found for "{searchInput}"</p>
                    )}
                  </div>
                )}
              </div>

              {/* UTILITIES (USER + CART) */}
              <div className="flex items-center gap-4 sm:gap-5 flex-1 lg:flex-initial justify-end flex-shrink-0">
                {user ? (
                  <div className="relative">
                    <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="w-7 h-7 rounded-full bg-green-800 border border-gold-500/40 flex items-center justify-center hover:border-gold-500 transition-colors relative z-50">
                      <span className="text-gold-400 text-xs font-body font-500">{user.name.charAt(0).toUpperCase()}</span>
                    </button>
                    
                    {/* 🟢 SOLID 100% OPAQUE USER DROPDOWN MENU */}
                    {userMenuOpen && (
                      <div className="absolute right-0 top-12 w-52 rounded-xl border p-2 shadow-2xl bg-[#04110c] border-green-800/60 z-[9999]">
                        
                        {/* Header Identity Block */}
                        <div className="px-4 py-2.5 border-b mb-1 border-green-800/20 bg-black/20 rounded-t-lg">
                          <p className="text-[#c9a84c] text-[9px] tracking-[0.2em] uppercase font-body font-semibold">Welcome Back</p>
                          <p className="text-white text-xs font-display truncate mt-0.5 font-medium tracking-wide">{user.name}</p>
                        </div>

                        {/* Navigation Links Cluster */}
                        <div className="space-y-0.5 font-body text-[11px] tracking-wide">
                          <Link 
                            to="/profile" 
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-white/80 hover:text-[#c9a84c] hover:bg-white/[0.04] rounded-lg transition-all"
                          >
                            <svg className="w-3.5 h-3.5 text-[#c9a84c]/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            My Profile
                          </Link>

                          <Link 
                            to="/my-orders" 
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-white/80 hover:text-[#c9a84c] hover:bg-white/[0.04] rounded-lg transition-all"
                          >
                            <svg className="w-3.5 h-3.5 text-[#c9a84c]/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                            My Orders
                          </Link>

                          <Link 
                            to="/wishlist" 
                            onClick={() => setUserMenuOpen(false)}
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-white/80 hover:text-[#c9a84c] hover:bg-white/[0.04] rounded-lg transition-all"
                          >
                            <svg className="w-3.5 h-3.5 text-[#c9a84c]/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                            Wishlist
                          </Link>
                        </div>

                        <div className="h-px bg-green-800/20 my-1.5" />

                        {/* Sign Out Action Button */}
                        <button 
                          onClick={handleLogout} 
                          className="flex items-center gap-2.5 w-full px-4 py-2.5 text-[10px] font-bold tracking-widest uppercase text-red-400 hover:bg-red-500/10 rounded-lg transition-all font-body text-left"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>

                      </div>
                    )}
                  </div>
                ) : (
                  <Link to="/login" className="text-cream/70 hover:text-gold-400 transition-colors flex items-center justify-center" aria-label="Sign In">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </Link>
                )}

                <Link to="/cart" className="relative p-1.5 flex items-center justify-center" aria-label="Cart">
                  <svg className="w-5 h-5 text-cream/70 hover:text-gold-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-[#c9a84c] text-[#04110c] text-[10px] font-extrabold w-4 h-4 rounded-full flex items-center justify-center shadow-md border border-[#04110c] z-10 leading-none">
                      {itemCount}
                    </span>
                  )}
                </Link>
              </div>

            </div>

            {/* DECK 3: BOTTOM NAVIGATION LINKS */}
            <div className="hidden lg:flex items-center justify-center gap-10 py-3.5 border-t border-white/5 bg-black/10">
              {[
                { label: 'Home', path: '/' },
                { label: 'Shop All', path: '/products' },
                { label: 'Men Collection', path: '/products?category=men' },
                { label: 'Women Collection', path: '/products?category=women' },
                { label: 'Unisex Luxury', path: '/products?category=unisex' },
                { label: 'Limited Edition', path: '/products?category=limited-edition' },
              ].map(({ label, path }) => (
                <Link key={label} to={path} className="text-cream/60 hover:text-gold-400 text-[11px] tracking-[0.2em] uppercase font-body transition-colors duration-300 relative group">
                  {label}
                  <span className="absolute -bottom-1 left-0 w-0 h-px bg-gold-500 group-hover:w-full transition-all duration-300" />
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </div>

      {/* MOBILE LEFT DRAWER */}
      {menuOpen && (
        <>
          <div className="lg:hidden fixed inset-0 top-0 z-[1050] bg-black/50 backdrop-blur-xs" onClick={() => setMenuOpen(false)} />
          <div className="lg:hidden fixed top-0 left-0 bottom-0 w-[75vw] sm:w-[50vw] max-w-sm z-[1100] bg-[#04110c]/98 backdrop-blur-xl border-r border-white/5 overflow-y-auto px-6 py-8 flex flex-col justify-start shadow-2xl">
            <div className="flex items-center justify-between pb-6 mb-6 border-b border-white/5">
              <span className="font-display text-lg text-cream tracking-wider">Menu</span>
              <button onClick={() => setMenuOpen(false)} className="text-cream/40 text-lg">✕</button>
            </div>
            <div className="flex flex-col gap-4">
              {[
                { label: 'Home', path: '/' },
                { label: 'Shop All Catalog', path: '/products' },
                { label: 'For Him', path: '/products?category=men' },
                { label: 'For Her', path: '/products?category=women' },
                { label: 'Unisex Blends', path: '/products?category=unisex' },
                { label: 'Limited Vault', path: '/products?category=limited-edition' },
              ].map(({ label, path }) => (
                <Link key={label} to={path} onClick={() => setMenuOpen(false)} className="text-[#f5efe4]/80 hover:text-gold-400 text-xs tracking-[0.2em] uppercase font-body transition-all duration-200 border-b border-white/[0.03] pb-3.5 block">
                  {label}
                </Link>
              ))}
            </div>
          </div>
        </>
      )}

      {/* 📱 FIXED OVERLAY: MOBILE POPUP SEARCH SCREEN OVERLAY FRAME */}
      {searchOpen && (
        <div 
          className="fixed inset-0 z-[2100] bg-[#04110c]/98 backdrop-blur-md flex items-start justify-center pt-24 px-6 animate-fade-in"
          onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          <div className="w-full max-w-2xl relative">
            <button 
              onClick={() => setSearchOpen(false)} 
              className="absolute -top-12 right-0 text-cream/50 hover:text-white font-body text-xs tracking-widest uppercase py-1"
            >
              ✕ Close Panel
            </button>
            <SearchBar onClose={() => setSearchOpen(false)} />
            <p className="text-cream/20 text-[10px] font-body text-center mt-4 tracking-wider uppercase">Tap background or button to exit scan</p>
          </div>
        </div>
      )}

      {userMenuOpen && <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />}
    </>
  );
}